import { TurnstileApi } from './turnstileController'

/**
 * Loads Cloudflare's api.js on demand, once, and hands back `window.turnstile`.
 *
 * The script must come from exactly this URL: proxying or bundling it breaks
 * the challenge. `render=explicit` stops it from scanning the page for
 * `.cf-turnstile` elements, so the controller decides when the widget runs.
 *
 * https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */

export const TURNSTILE_ONLOAD = 'onTurnstileLoad'

const SCRIPT_URL = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_ONLOAD}`

declare global {
  interface Window {
    turnstile?: TurnstileApi
    [TURNSTILE_ONLOAD]?: () => void
  }
}

let loading: Promise<TurnstileApi> | null = null

export const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    window[TURNSTILE_ONLOAD] = () => {
      delete window[TURNSTILE_ONLOAD]
      if (window.turnstile) resolve(window.turnstile)
      else reject(new Error('Turnstile script loaded without exposing its API'))
    }

    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.defer = true
    script.addEventListener('error', () => {
      // Leave nothing behind, so the next call can try again.
      loading = null
      delete window[TURNSTILE_ONLOAD]
      script.remove()
      reject(new Error('Turnstile script failed to load'))
    })
    document.head.appendChild(script)
  })

  return loading
}
